import { useCallback } from 'react';
import debug from 'debug';
import { versionManager } from '../utils/versionManager';

// Configuración de logs con namespaces específicos para el hook
const logVersionHook = debug('rois:version:hook:manager');

// Habilitar logs solo en desarrollo
if (import.meta.env.DEV) {
  debug.enable('rois:version*');
}

/**
 * Acceso desde componentes al gestor de versiones.
 *
 * 🔴 **Este hook NO es por donde la app detecta una versión nueva.** Eso lo
 * hace `useAppUpdate` (en `TimerOverlay`), en web y en la APK: en web compara
 * con el `/version.json` del dominio, y el `onNeedRefresh` del Service Worker
 * (`main.tsx`) solo le pide una comprobación; en la APK manda el OTA
 * (`latest.json`). El arranque en frío (`versionManager.initialize()`) solo
 * re-siembra la versión, y el sondeo al backend está apagado (`App.tsx`).
 *
 * Lo único que se expone aquí es la limpieza MANUAL, que es lo único que un
 * componente necesita disparar: el botón "Limpiar caché" del menú de usuario.
 *
 * Antes había nueve funciones más (comprobar versión, corrupción de caché,
 * configurar y sondear GitHub Deployments…). Ninguna tenía un solo consumidor,
 * y ninguna llevaba el gate de producción que sí tienen los tres caminos
 * reales — eran una puerta abierta a meter avisos de versión en beta sin
 * enterarse. Para depurar a mano está `VersionManagerDebug` en la consola
 * (solo en desarrollo, ver el final de `versionManager.ts`).
 */
export const useVersionManager = () => {
  /**
   * Fuerza la limpieza de cachés.
   *
   * Sin gate de entorno ni de plataforma, a propósito: aquí el usuario ha
   * pedido limpiar explícitamente. Solo vacía lo reconstruible
   * (`clearRebuildableCaches`): las colas sin conexión, OPFS y los borradores
   * —trabajo del operario todavía sin subir— se conservan.
   */
  const forceCacheClear = useCallback(async (reason?: string) => {
    try {
      await versionManager.forceCacheClear(reason);
      // La recarga de página ahora la maneja el modal automáticamente
    } catch (error) {
      logVersionHook('Error forcing cache clear:', error);
    }
  }, []);

  return { forceCacheClear };
};
